import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { FaTrash, FaCheckCircle, FaTimesCircle, FaPlus, FaCheckDouble, FaUpload, FaArrowRight, FaChevronDown } from 'react-icons/fa'
import { LuEllipsisVertical } from 'react-icons/lu'
import { useParams, Link as RouterLink } from 'react-router'
import { Badge, Heading, Text, Box, Button, Icon, Input, Textarea, Select, Stack, Tag, SimpleTable, EmptyBox, EmptyCard, ConfirmButton, FileChooser, SimplePopover, SkeletonBulletList, FormControl, UserChip, Alert, Tree, Link as ChakraLink, Menu, MenuItem, SubMenu, SimpleModal, Checkbox, Checkmark } from '@/components/lib'
import api from '../../api'
import utils from '../../utils/utils'
import useStore from '../../store'
import useTitle from '../../hooks/useTitle'

import FilesImage from '../../images/files.png'
import NotFoundImage from '../../images/notFound.png'

export const FILE_TYPES = [
  { key: 'amendments', name: 'Amendments' },
  { key: 'crf', name: 'Case Report Form (CRF)' },
  { key: 'contract', name: 'Contract' },
  { key: 'correspondence', name: 'Correspondence' },
  { key: 'cv', name: 'CV' },
  { key: 'ethics', name: 'Ethics' },
  { key: 'financial', name: 'Financial' },
  { key: 'hra', name: 'Health Research Authority' },
  { key: 'laboratory', name: 'Laboratory' },
  { key: 'legal', name: 'Legal' },
  { key: 'managementDocumentation', name: 'Management Documentation' },
  { key: 'monitoringAndAudit', name: 'Monitoring and Audit' },
  { key: 'other', name: 'Other' },
  { key: 'participants', name: 'Participants' },
  { key: 'plans', name: 'Plans' },
  { key: 'protocol', name: 'Protocol' },
  { key: 'regulatory', name: 'Regulatory' },
  { key: 'reports', name: 'Reports' },
  { key: 'researchAndDevelopment', name: 'Research and Development' },
  { key: 'sop', name: 'SOP' },
  { key: 'staff', name: 'Staff' },
  { key: 'workingInstruction', name: 'Working Instruction' }
]

export const FILE_STATUSES = [
  { key: 'draft', name: 'Draft', colour: 'orange' },
  { key: 'review', name: 'Review', colour: 'yellow' },
  { key: 'approved', name: 'Approved', colour: 'green' },
  { key: 'rejected', name: 'Rejected', colour: 'red' }
]

export default function Files () {
  const [loading, setLoading] = useState(false)
  const [generatingReport, setGeneratingReport] = useState(false)
  const [files, setFiles] = useState([])
  const [nameFilter, setNameFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState()
  const [statusFilter, setStatusFilter] = useState()
  const [siteFilter, setSiteFilter] = useState()
  const [fileImporterOpen, setFileImporterIsOpen] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [droppedFiles, setDroppedFiles] = useState([])
  const [droppedSite, setDroppedSite] = useState()
  const [selectedFiles, setSelectedFiles] = useState([])
  const [selectedVersionViewerFile, setSelectedVersionViewerFile] = useState(null)
  const [selectedApprovalViewerFile, setSelectedApprovalViewerFile] = useState(null)
  const [filesToCopy, setFilesToCopy] = useState(null)
  const { id } = useParams()
  const { user, trial, sites } = useStore()
  useTitle('Document Control', trial)

  useEffect(() => {
    setLoading(true)
    api.files.getForTrial(id, d => {
      setFiles(d.files || [])
      setLoading(false)
    })
  }, [id])

  function onFileCreated (newFile) {
    setFiles(prev => [newFile, ...prev])
  }

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])
  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false)
    }
  }, [])
  const handleDrop = useCallback((e, siteId) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    setDroppedFiles(Array.from(e.dataTransfer.files))
    setDroppedSite(siteId)
    setFileImporterIsOpen(true)
  }, [])

  const updateFile = useCallback((fileId, data) => {
    return new Promise((resolve, reject) => {
      api.files.update(fileId, data, updatedFile => {
        setFiles(prev => prev.map(f => {
          if (f._id === updatedFile._id) {
            return { ...f, ...updatedFile }
          }
          return f
        }))
        resolve(updatedFile)
      }, err => {
        utils.toast('error', 'Unable to update file', err.message)
        reject(err)
      })
    })
  }, [files])

  const deleteFile = useCallback((fileId) => {
    return new Promise((resolve, reject) => {
      api.files.delete(fileId, () => {
        utils.toast('success', 'File deleted')
        setFiles(prev => prev.filter(f => f._id !== fileId))
        resolve(files)
      }, err => {
        utils.toast('error', 'Unable to delete file', err.message)
        reject(err)
      })
    })
  }, [files])

  const approveFile = useCallback((fileId) => {
    const latestVersion = files.find(f => f._id === fileId)?.latestVersion
    if (!latestVersion) return utils.toast('error', 'No version available to approve')

    // Here we create the approval with the current user as the approver.
    // We then immediately pass an approval token to auto-approve it.
    api.files.createApproval(fileId, {
      approvers: [{
        id: user._id,
        email: user.email,
        role: 'Auto approver'
      }],
      message: 'MESSAGE: This file will be auto-approved. No action is required.'
    }, approval => {
      if (approval.approvalToken) {
        api.files.updateApproverApproval(fileId, approval._id, {
          approved: true,
          comment: 'Automatically approved by user'
        }, approval.approvalToken, () => {
          utils.toast('success', 'File approved')
          api.files.get(fileId, (updatedFile) => {
            setFiles(files.map(f => {
              if (f._id === updatedFile?._id) {
                return updatedFile
              }
              return f
            }))
          })
        }, err => {
          utils.toast('error', 'Unable to approve file', err.message)
        })
      }
    }, err => {
      utils.toast('error', 'Unable to submit for approval', err.message)
    })
  }, [files, user])

  function cancelPendingApproval (file) {
    api.files.listApprovalsForFile(file._id, ({ approvals }) => {
      const pendingApproval = approvals.find(a => a.status === 'pending')
      if (!pendingApproval) return utils.toast('info', 'No pending approval to cancel')

      api.files.deleteApproval(file._id, pendingApproval._id, () => {
        utils.toast('success', 'Pending approval cancelled')
        setFiles(files?.map(f => {
          if (f._id === file._id) return { ...f, status: 'draft' }
          return f
        }))
      }, err => {
        utils.toast('error', 'Unable to cancel pending approval', err.message)
      })
    }, err => {
      utils.toast('error', 'Unable to list approvals for file', err.message)
    })
  }

  function generateReport () {
    setGeneratingReport(true)
    api.files.createReport(id, (report) => {
      utils.downloadFile(report.url, `trial-${id}-files-report.pdf`)
      setGeneratingReport(false)
    }, err => {
      utils.toast('error', 'Unable to generate report', err.message)
      setGeneratingReport(false)
    })
  }

  const filteredFiles = useMemo(() => {
    if (!files) return []
    let filtered = files || []
    if (nameFilter) filtered = filtered.filter(f => f.name?.toLowerCase().includes(nameFilter) || f.fileName?.toLowerCase().includes(nameFilter))
    if (typeFilter) filtered = filtered.filter(f => f.type === typeFilter)
    if (statusFilter) {
      if (statusFilter === 'unapproved') filtered = filtered.filter(f => f.status !== 'approved')
      else filtered = filtered.filter(f => f.status === statusFilter)
    }
    if (siteFilter && siteFilter !== 'trial') filtered = filtered.filter(f => f.site === siteFilter)
    else if (siteFilter === 'trial') filtered = filtered.filter(f => !f.site)
    return filtered
  }, [files, nameFilter, typeFilter, statusFilter, siteFilter])

  const generateTreeEntry = useCallback((file) => {
    if (!file) return null
    const lastVersion = file.latestVersion
    return {
      id: file._id,
      name: file.fileName || file.name,
      display:
  <Stack
    direction='row' alignItems='center' onClick={e => {
      setSelectedFiles(prev => {
        if (prev.includes(file._id)) return prev.filter(f => f !== file._id)
        return [...prev, file._id]
      })
    }}
  >
    <Checkmark checked={selectedFiles.includes(file._id)} size='xs' />
    <Stack direction='row' onClick={e => e.stopPropagation()} alignItems='center'>
      <Text><ChakraLink as={RouterLink} to={`/trials/${id}/files/${file._id}`} colorPalette='primary' variant='underline'>{file.fileName || file.name}</ChakraLink></Text>
      {file.currentVersion &&
        <Box onClick={() => setSelectedApprovalViewerFile(file)} cursor='pointer' title='View approval history'>
          <Badge colorPalette={FILE_STATUSES.filter(s => s.key === file.status)[0]?.colour}>
            {file.currentVersion}
            {file.status === 'review' && <span>(in review)</span>}
            {file.status === 'rejected' && <Icon><FaTimesCircle /></Icon>}
            {file.status === 'approved' && <Icon><FaCheckCircle /></Icon>}
          </Badge>
        </Box>}
      {lastVersion?.hash && (
        <Box
          fontFamily='monospace'
          {...(utils.canReadFileVersions(user, trial, file) && { cursor: 'pointer', onClick: () => setSelectedVersionViewerFile(file) })}
        >
          <Tag size='sm' colorPalette='gray'>{lastVersion.hash.slice(0, 8)}</Tag>
        </Box>
      )}
      {file.type && <Badge colorPalette='blue'>{FILE_TYPES.filter(t => t.key === file.type)[0]?.name || file.type}</Badge>}
      {file.tags?.map(t => <Tag key={t} size='sm' colorPalette='gray'>{t}</Tag>)}
      {file.assigneeUsers?.map(u => <UserChip key={u._id} user={u} onlyAvatar compact />)}
    </Stack>
  </Stack>,
      actions:
  <Stack direction='row' alignItems='center'>
    <VersionCreator
      trial={trial} file={file} onChange={d => {
        setFiles(files.map(f => {
          if (f._id === file._id) return Object.assign({}, f, d)
          return f
        }))
      }}
      trigger={<Button size='2xs' variant='ghost'><Icon><FaPlus /></Icon></Button>}
    />
    {['draft', 'rejected'].includes(file.status) &&
      <ApprovalSender
        trial={trial} file={file} onChange={(d) => {
          setFiles(files.map(f => {
            if (f._id === file._id) return Object.assign({}, f, d)
            return f
          }))
        }} trigger={<Button size='2xs' variant='ghost'><Icon as={FaCheckDouble} /></Button>}
      />}
    <Menu
      trigger={<Button size='xs' variant='ghost'><Icon as={LuEllipsisVertical} /></Button>}
    >
      {lastVersion?.url && <MenuItem value='view' as='a' href={lastVersion?.url} target='_blank' rel='noopener noreferrer'>View latest version</MenuItem>}
      {utils.canWriteFile(user, trial, file) &&
        <>
          <SubMenu trigger='Move file...'>
            <MenuItem value='trial' onClick={() => updateFile(file._id, { site: null })}>
              {!file.site ? <Icon as={FaCheckCircle} /> : <Box width='1.25em' display='inline-block' />}
              Trial-level
            </MenuItem>
            {sites?.map(site =>
              <MenuItem key={site._id} value={site._id} onClick={() => updateFile(file._id, { site: site._id })}>
                {file.site === site._id ? <Icon as={FaCheckCircle} /> : <Box width='1.25em' display='inline-block' />}
                {site.name}
              </MenuItem>)}
          </SubMenu>
          <SubMenu trigger='Type'>
            {FILE_TYPES.map(t =>
              <MenuItem key={t.key} value={t.key} onClick={() => updateFile(file._id, { type: t.key })}>
                {file.type === t.key ? <Icon as={FaCheckCircle} /> : <Box width='1.25em' display='inline-block' />}
                {t.name}
              </MenuItem>)}
          </SubMenu>
        </>}
      {(utils.canWriteTrialFiles(user, trial) || sites?.some(s => utils.canWriteSiteFiles(user, trial, s))) &&
        <MenuItem value='copy' onClick={() => setFilesToCopy([file])}>Copy to site…</MenuItem>}
      {utils.canReadFileVersions(user, trial, file) &&
        <MenuItem value='versions' onClick={() => setSelectedVersionViewerFile(file)}>Version history</MenuItem>}
      <MenuItem value='approvals' onClick={() => setSelectedApprovalViewerFile(file)}>Approval history</MenuItem>
      {file.status === 'review' &&
        <ConfirmButton
          asSubMenu
          onConfirm={() => cancelPendingApproval(file)}
          actionColor='red'
          trigger='Cancel approval'
          header='Really cancel this approval?'
          body='This will remove the pending approval request. Approvers will be automatically notified.'
        />}
      {utils.canApproveFile(user, trial, file) && file.status !== 'approved' &&
        <ConfirmButton
          asSubMenu
          onConfirm={() => approveFile(file._id)}
          actionColor='green'
          trigger='Approve file'
          header='Really approve this file?'
          body='This will make a copy the latest version of this file and mark it as "approved".'
        />}
      {utils.canDeleteFile(user, trial, file) &&
        <ConfirmButton
          asSubMenu
          onConfirm={() => deleteFile(file._id)} actionColor='red'
          trigger='Delete file'
          header='Really delete this file?'
          body='This file and all of its versions will be irretrievably removed.'
        />}
    </Menu>
  </Stack>
    }
  }, [user, trial, files, selectedFiles])

  const treeStructure = useMemo(() => {
    const ts = []
    filteredFiles?.filter(f => !f.site)?.forEach(file => ts.push(generateTreeEntry(file)))
    sites?.forEach(site => {
      const siteFiles = filteredFiles?.filter(f => f.site === site._id)
      if (!siteFiles || siteFiles.length === 0) return
      ts.push({
        id: site._id,
        name: site.name,
        display: <Text onDrop={e => handleDrop(e, site._id)}>{site.name}</Text>,
        children: siteFiles?.map(file => generateTreeEntry(file))
      })
    })
    return ts
  }, [filteredFiles, sites, selectedFiles])

  return (
    <Stack gap={5}>
      <Stack direction='row' justifyContent='space-between'>
        <Box>
          <Heading as='h2' size='lg'>Documents</Heading>
          <Text>Documents and files associated with this trial.</Text>
        </Box>
        <Stack direction='row' alignItems='center'>
          <Button variant='ghost' isLoading={generatingReport} onClick={generateReport}>Download report</Button>
          <Button colorPalette='primary' onClick={() => setFileImporterIsOpen(true)}>Add files <Icon><FaUpload /></Icon></Button>
        </Stack>
      </Stack>

      {loading
        ? <SkeletonBulletList />
        : ((files?.length > 0)
            ? (
              <Stack onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} borderWidth='1px' borderColor='border.default' p={2} rounded='md' bg={isDragOver ? 'bg.accent' : 'bg.surface'} shadow='md'>
                <Stack p={2} rounded='md' gap={5}>
                  <Stack direction='row' alignItems='center'>
                    <Input size='sm' placeholder='Search files...' value={nameFilter} onChange={e => setNameFilter(e.target.value?.toLowerCase())} />
                    {sites?.length > 0 &&
                      <Select size='sm' placeholder='Everywhere...' value={siteFilter} onChange={e => setSiteFilter(e.target.value)}>
                        <option value='trial'>Trial-level (not site-specific)</option>
                        {sites.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
                      </Select>}
                    <Select size='sm' placeholder='All types...' value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
                      {FILE_TYPES.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                    </Select>
                    <Select size='sm' placeholder='All statuses...' value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                      {FILE_STATUSES.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                      <option value='unapproved'>Unapproved</option>
                    </Select>

                    {selectedFiles.length > 0 &&
                      <Menu trigger={<Button size='xs' variant='ghost'>{selectedFiles?.length || 0} selected <Icon as={FaChevronDown} /></Button>}>
                        <MenuItem value='deselect' onClick={() => setSelectedFiles([])}>Deselect all</MenuItem>
                        <SubMenu trigger='Move files'>
                          <MenuItem
                            value='trial' onClick={() => {
                              Promise.all(selectedFiles.map(fileId => updateFile(fileId, { site: null })))
                                .then(() => setSelectedFiles([]))
                                .catch(err => utils.toast('error', 'Unable to update selected files', err.message))
                            }}
                          >
                            Trial-level
                          </MenuItem>
                          {sites?.map(site =>
                            <MenuItem
                              key={site._id} value={site._id} onClick={() => {
                                Promise.all(selectedFiles.map(fileId => updateFile(fileId, { site: site._id })))
                                  .then(() => setSelectedFiles([]))
                                  .catch(err => utils.toast('error', 'Unable to update selected files', err.message))
                              }}
                            >
                              {site.name}
                            </MenuItem>)}
                        </SubMenu>
                        <MenuItem
                          value='copy'
                          onClick={() => setFilesToCopy(selectedFiles.map(fileId => files.find(f => f._id === fileId)).filter(Boolean))}
                        >
                          Copy to site…
                        </MenuItem>
                        <ConfirmButton
                          asSubMenu
                          onConfirm={() => {
                            Promise.all(selectedFiles.map(fileId => deleteFile(fileId)))
                              .then(() => setSelectedFiles([]))
                              .catch(err => utils.toast('error', 'Unable to delete selected files', err.message))
                          }}
                          actionColor='red'
                          trigger='Delete files'
                          header='Really delete selected files?'
                          body='This will irretrievably remove the selected files and all of their versions.'
                        />
                      </Menu>}
                  </Stack>

                  {filteredFiles?.length === 0 &&
                    <EmptyBox image={NotFoundImage} title='No files found matching your filters' description='Please try adjusting your search terms.' />}

                  <Tree structure={treeStructure} />

                  <Stack direction='row' justifyContent='center'>
                    <EmptyCard icon='files' title='Documents and files' description='The document control feature allows you to upload files, manage versions, handle approvals, and more.' />
                  </Stack>
                </Stack>
              </Stack>)
            : (
              <Stack gap={5} alignItems='center'>
                <EmptyCard icon='files' title='Documents and files' description='The document control feature allows you to upload files, manage versions, handle approvals, and more. Get started by adding your first file.' />

                <EmptyBox
                  onDrop={handleDrop}
                  title='Add your first file' description='Create a new file entry using the button below or dragging files into this pane.'
                >
                  <Button colorPalette='primary' onClick={() => setFileImporterIsOpen(true)}>Add your first files</Button>
                </EmptyBox>
              </Stack>
              )
          )}

      <FileImporter
        trial={trial} newFiles={droppedFiles} site={droppedSite} onFileCreated={onFileCreated} isOpen={fileImporterOpen} setIsOpen={isOpen => {
          if (!isOpen) {
            setDroppedFiles([])
            setDroppedSite()
          }
          setFileImporterIsOpen(isOpen)
        }}
      />
      <VersionViewer user={user} trial={trial} file={selectedVersionViewerFile} isOpen={!!selectedVersionViewerFile} onClose={() => setSelectedVersionViewerFile(null)} />
      <ApprovalViewer file={selectedApprovalViewerFile} isOpen={!!selectedApprovalViewerFile} onClose={() => setSelectedApprovalViewerFile(null)} />
      <CopyToSiteDialog
        files={filesToCopy} isOpen={!!filesToCopy} onClose={() => setFilesToCopy(null)}
        onCopied={created => {
          setFiles(prev => [...(created || []), ...prev])
          setSelectedFiles([])
        }}
      />
    </Stack>

  )
}

function ApprovalSender ({ trigger, trial, file, onChange }) {
  const [approvers, setApprovers] = useState([])
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const { user } = useStore()

  function submit () {
    setSubmitting(true)
    api.files.createApproval(file._id, {
      approvers,
      message
    }, approval => {
      setSubmitting(false)
      utils.toast('success', 'File submitted for approval')
      setApprovers([])
      setMessage('')
      onChange({ status: 'review' })
    }, err => {
      setSubmitting(false)
      utils.toast('error', 'Unable to submit for approval', err.message)
    })
  }

  function addApprover (email, role) {
    setApprovers([...approvers, { id: utils.uuidv4(), email, role }])
  }

  function updateApprover (id, email, role) {
    setApprovers(approvers.map(a => {
      if (a.id === id) return { ...a, email, role }
      return a
    }))
  }

  if (!utils.canRequestFileApproval(user, trial, file)) return null

  return (
    <SimplePopover
      closeOnBlur={false}
      trigger={trigger || <Button colorPalette='primary'>Submit for approval</Button>}
      header='Submit this file for approval'
      contentProps={{ style: { width: '400px' } }}
      body={
        <Stack gap={5}>
          {approvers.length === 0 && <Text color='fg.subtle' textAlign='center'>No reviewers added yet.</Text>}
          {approvers.length > 0 &&
            <SimpleTable
              headers={['Email address', 'Role', '']}
              rows={approvers.map(a => [
                <Input key={a.id} size='xs' placeholder='jane@example.com' value={a.email} onChange={e => updateApprover(a.id, e.target.value, a.role)} />,
                <Input key={a.id} size='xs' placeholder='Sponsor Lead' value={a.role} onChange={e => updateApprover(a.id, a.email, e.target.value)} />,
                <Button key={a.id} size='xs' variant='ghost' onClick={() => setApprovers(approvers.filter(ap => ap.id !== a.id))}><Icon><FaTrash /></Icon></Button>
              ])}
            />}
          <Button size='xs' onClick={() => addApprover('', '')}>Add reviewer</Button>

          <FormControl label='Message to reviewers' helperText='Optional. This message will be included in the invitation email.'>
            <Textarea placeholder='Please review this file and provide your feedback...' value={message} onChange={e => setMessage(e.target.value)} />
          </FormControl>

          <Alert>The latest version of this file will be sent for approval. All reviewers must approve the file for it to be accepted and marked as "approved".</Alert>

          <Button colorPalette='primary' isLoading={submitting} onClick={submit}>Submit for approval</Button>
        </Stack>
      }
    />
  )
}

function VersionCreator ({ trigger, trial, file, onChange }) {
  const [newVersionFile, setNewVersionFile] = useState()
  const { user } = useStore()

  function addVersion () {
    if (!newVersionFile) return
    api.files.createVersion(file._id, {
      name: newVersionFile.name,
      storedName: newVersionFile.storedName
    }, newV => {
      onChange({ fileName: newVersionFile.name, status: 'draft', currentVersion: newV.version, latestVersion: newV })
      utils.toast('success', 'New version created')
      setNewVersionFile()
    }, err => utils.toast('error', 'Unable to create version', err.message))
  }

  if (!utils.canWriteFile(user, trial, file)) return null

  return (
    <SimplePopover
      closeOnBlur={false}
      header='Create a new version'
      trigger={trigger}
      body={
        <Stack textAlign='left'>
          {file.status === 'approved' &&
            <Alert status='info'>This file is already approved. By creating a new version you will move this file back into a "draft" status.</Alert>}

          {file.status === 'review' &&
            <Alert status='error'>This file is currently under review. Please cancel any ongoing approval reviews before creating a new version.</Alert>}

          {newVersionFile &&
            <Tag size='xl' label={newVersionFile.name} onClose={e => setNewVersionFile()} />}

          {!newVersionFile &&
            <FileChooser
              forType='trial' forObject={trial}
              onComplete={setNewVersionFile}
              trigger={<Button size='sm'>Upload a file</Button>}
            />}

          {newVersionFile && file.fileName && newVersionFile.name !== file.fileName &&
            <Alert status='error'>The name of this file must match the name of previous versions: {file.fileName}.</Alert>}

          <Text color='fg.subtle' fontSize='sm'>Make sure that you are not uploading any restricted or sensitive information in your file, such as personal participant or patient information.</Text>

          {newVersionFile && <Button onClick={addVersion} size='sm' colorPalette='primary'>Create version</Button>}
        </Stack>
      }
    />
  )
}

function FileImporter ({ trial, newFiles, site, onFileCreated, isOpen, setIsOpen }) {
  const [filesToCreate, setFilesToCreate] = useState([])
  const [uploading, setUploading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const { user, sites } = useStore()

  useEffect(() => {
    if (newFiles.length > 0) {
      setFilesToCreate(newFiles.map(fileMapper))
      setIsOpen(true)
      setUploading(false)
      setSuccess(false)
    }
  }, [newFiles, site])

  const fileMapper = useCallback((file) => ({
    name: file.name,
    sites: site ? [site] : [''],
    type: 'other',
    isPending: false,
    isLoading: false,
    isComplete: false,
    handle: file,
    status: 'draft'
  }), [site])

  const handleDragOver = useCallback((e) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])
  const handleDragLeave = useCallback((e) => {
    e.preventDefault()
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setIsDragOver(false)
    }
  }, [])
  const handleDrop = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    const droppedFiles = Array.from(e.dataTransfer.files)
    const newFiles = droppedFiles.map(fileMapper)
    setFilesToCreate(prev => [...prev, ...newFiles])
    setSuccess(false)
  }, [])

  async function uploadFile (file) {
    // Upload the object once, then fan out into one independent file doc per
    // selected destination site (each gets its own copied S3 object server-side).
    return new Promise((resolve, reject) => {
      const targets = file.sites && file.sites.length > 0 ? file.sites : ['']
      utils.uploadFile(file.handle, 'trial', trial._id, data => {
        api.files.createBatch(trial._id, {
          name: file.name,
          type: file.type,
          storedName: data.storedName,
          status: file.status || 'draft',
          sites: targets
        }, ({ files: createdFiles }) => {
          (createdFiles || []).forEach(cf => onFileCreated(cf))
          setFilesToCreate(prev => prev.map(f => {
            if (f.name === file.name) return { ...f, isLoading: false, isPending: false, isComplete: true }
            return f
          }))
          resolve(createdFiles)
        }, err => {
          utils.toast('error', `Unable to create file ${file.name}`, err.message)
          setUploading(false)
          reject(err)
        })
      }, err => {
        utils.toast('error', `Unable to upload file ${file.name}`, err.message)
        setUploading(false)
        reject(err)
      })
    })
  }

  async function confirmImport () {
    setFilesToCreate(filesToCreate.map(f => ({
      ...f, isPending: true, isLoading: false, isComplete: false
    })))

    setUploading(true)
    for (const file of filesToCreate) {
      setFilesToCreate(prev => prev.map(f => {
        if (f.name === file.name) return { ...f, isLoading: true }
        return f
      }))
      await uploadFile(file)
    }
    setUploading(false)
    setSuccess(true)
    setFilesToCreate([])
    setIsOpen(false)
  }

  return (
    <SimpleModal
      size='xl'
      isOpen={isOpen} onClose={() => setIsOpen(false)}
      header='Upload files to the document control system'
      body={
        <Stack gap={5} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} rounded='sm' p={3} bg={isDragOver ? 'bg.accent' : 'initial'}>
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <Text>Trialflare will create a new file entry for each file you upload.</Text>
            <Stack direction='row'>
              <FileChooser
                forType='trial' forObject={trial} asFileInput multiple
                onComplete={files => {
                  const newFiles = [...filesToCreate]
                  for (const file of files) {
                    if (newFiles.find(f => f.name === file.name)) continue
                    newFiles.push(fileMapper(file))
                  }
                  setFilesToCreate(newFiles)
                  setSuccess(false)
                }}
                trigger={<Button colorPalette='primary' variant='outline'>Add files</Button>}
              />
              {filesToCreate?.length > 0 && <Button onClick={() => setFilesToCreate([])} variant='ghost'>Clear</Button>}
            </Stack>
          </Box>

          {(!filesToCreate || filesToCreate.length === 0) &&
            <EmptyBox
              title='No files to upload'
              description='Please select files to upload from your computer or drag them into this pane.'
              image={FilesImage}
            />}

          {filesToCreate?.length > 0 && (
            <SimpleTable
              size='sm'
              headers={['File name', 'Site', 'Type', utils.canApproveFile(user, trial, null) ? 'Approved' : '', '']}
              rows={filesToCreate.map(file => [
                <Stack direction='row' alignItems='center' key={file.name}>
                  <Text key={`${file.name}-status`}>{file.isLoading ? <FaUpload /> : file.isPending ? <Badge size='xs' colorPalette='yellow'>Ready</Badge> : file.isComplete ? <FaCheckCircle /> : <Badge size='xs' colorPalette='yellow'>Ready</Badge>}</Text>
                  <Text key={file.name}>{file.name}</Text>
                </Stack>,
                <SiteMultiSelect
                  key={`${file.name}-site`}
                  value={file.sites || []}
                  trialWritable={utils.canWriteTrialFiles(user, trial)}
                  sites={sites?.filter(s => utils.canWriteSiteFiles(user, trial, s)) || []}
                  onChange={newSites => setFilesToCreate(filesToCreate.map(f => {
                    if (f.name === file.name) return { ...f, sites: newSites }
                    return f
                  }))}
                />,
                <Select
                  key={`${file.name}-type`} value={file.type} onChange={e => setFilesToCreate(filesToCreate.map(f => {
                    if (f.name === file.name) return { ...f, type: e.target.value }
                    return f
                  }))}
                >
                  {FILE_TYPES.map(t => <option key={t.key} value={t.key}>{t.name}</option>)}
                </Select>,
                (utils.canApproveFile(user, trial, file)
                  ? (
                    <Checkbox
                      key={`${file.name}-status`} isChecked={file.status === 'approved'} onChange={e => setFilesToCreate(filesToCreate.map(f => {
                        if (f.name === file.name) return { ...f, status: e.checked ? 'approved' : 'draft' }
                        return f
                      }))}
                    />)
                  : <Box key={`${file.name}-status`} width='1.25em' display='inline-block' />),
                <ConfirmButton
                  key={`${file.name}-delete`}
                  trigger={<Button size='xs' variant='ghost'><Icon as={FaTrash} /></Button>}
                  header='Really delete this file?'
                  body='This file will be removed from the upload list.'
                  onConfirm={() => setFilesToCreate(filesToCreate.filter(f => f.name !== file.name))}
                />
              ])}
            />
          )}
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end'>
          <Button onClick={() => setIsOpen(false)}>Close</Button>
          {!success && filesToCreate?.length > 0 && <Button isLoading={uploading} colorPalette='primary' onClick={confirmImport}>Upload {filesToCreate?.length || 0} file{filesToCreate?.length > 1 && 's'}</Button>}

        </Stack>
      }
    />
  )
}

function VersionViewer ({ user, trial, file, isOpen, onClose }) {
  const [versions, setVersions] = useState([])
  const { getTrialUsers } = useStore()
  const trialUsers = getTrialUsers(trial?._id)
  const canReadVersions = file && trial && utils.canReadFileVersions(user, trial, file)

  useEffect(() => {
    if (!file || !canReadVersions) return
    setVersions([])
    api.files.listVersionsForFile(file._id, data => {
      setVersions(data.versions || [])
    }, err => {
      utils.toast('error', 'Unable to list versions for file', err.message)
    })
  }, [file, canReadVersions])

  if (!file) return null
  return (
    <SimpleModal
      size='lg'
      isOpen={isOpen} onClose={onClose}
      header={`${file.name} Version History`}
      body={
        <Stack gap={5}>
          {!canReadVersions && (
            <Alert status='warning'>You don't have permission to view file versions.</Alert>
          )}
          {canReadVersions && (
            <SimpleTable
              headers={['Version', 'Status', 'Created by', 'Created at', '']}
              rows={versions?.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.map(v => {
                const createdByUser = trialUsers?.find(u => u._id === v.createdBy) || { _id: v.createdBy }
                return [
                  <Stack key={v.version} fontFamily='monospace'>
                    <Text>{v.version}</Text>
                    {v.hash && <Tag size='sm' colorPalette='gray'>{v.hash.slice(0, 16)}</Tag>}
                  </Stack>,
                  <Badge key={v.status} colorPalette={FILE_STATUSES.filter(s => s.key === v.status)[0]?.colour}>{v.status}</Badge>,
                  <UserChip compact key={v.createdBy} user={createdByUser} />,
                  <Text key={v.createdAt}>{utils.formatDateTime(v.createdAt)}</Text>,
                  <Stack direction='row' gap={2} key={`${v._id}-actions`}>
                    {v.url && <Button size='xs' as='a' href={v.url} target='_blank' rel='noopener noreferrer'>View</Button>}
                  </Stack>
                ]
              })}
            />
          )}
        </Stack>
      }
    />
  )
}

function ApprovalViewer ({ file, isOpen, onClose }) {
  const [approvals, setApprovals] = useState([])

  useEffect(() => {
    if (!file) return
    setApprovals([])
    api.files.listApprovalsForFile(file._id, data => {
      setApprovals(data.approvals || [])
    }, err => {
      utils.toast('error', 'Unable to list approvals for file', err.message)
    })
  }, [file])

  if (!file) return null
  return (
    <SimpleModal
      size='xl'
      isOpen={isOpen} onClose={onClose}
      header={`${file.name} Approval History`}
      body={
        <Stack gap={5}>
          {(!approvals || approvals?.length === 0) &&
            <EmptyBox image={NotFoundImage} title='No approvals found' description='This file has not been submitted for approval yet.' />}
          {approvals?.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))?.map(a =>
            <Stack key={a._id} bg='bg.surface' p={3} borderRadius='md'>
              <Stack direction='row' alignItems='center' gap={3}>
                <Badge>{a.fileVersion}</Badge>
                {a.nextFileVersion && <Badge colorPalette='blue'><FaArrowRight /> {a.nextFileVersion}</Badge>}
                <Badge colorPalette={FILE_STATUSES.filter(s => s.key === a.status)[0]?.colour}>{a.status}</Badge>
              </Stack>
              <SimpleTable
                headers={['Approver', 'Role', 'Status', 'Status at', 'Message']}
                rows={a.approvers?.map(approver => {
                  return [
                    approver.email,
                    approver.role || 'N/A',
                    <Badge key={approver._id} colorPalette={FILE_STATUSES.filter(s => s.key === approver.status)[0]?.colour}>{approver.status || 'pending'}</Badge>,
                    utils.formatDateTime(approver.statusChangedAt || a.createdAt),
                    <Text key={approver._id} whiteSpace='pre-line'>{approver.comment || 'N/A'}</Text>
                  ]
                })}
              />
            </Stack>
          )}
        </Stack>
      }
    />
  )
}

// Multi-select of destination sites (plus "Trial-level"), used when uploading a
// file to several sites at once. `value` is an array of site ids where '' means
// trial-level.
function SiteMultiSelect ({ value, trialWritable, sites, onChange }) {
  function label () {
    if (!value || value.length === 0) return 'Select destinations...'
    const names = value.map(v => v === '' ? 'Trial-level' : (sites.find(s => s._id === v)?.name || 'Site'))
    return names.length === 1 ? names[0] : `${names.length} destinations`
  }
  function toggle (v) {
    if (value.includes(v)) onChange(value.filter(x => x !== v))
    else onChange([...value, v])
  }
  return (
    <Menu closeOnSelect={false} trigger={<Button size='xs' variant='outline'>{label()} <Icon as={FaChevronDown} /></Button>}>
      {trialWritable &&
        <MenuItem value='' onClick={() => toggle('')}>
          {value.includes('') ? <Icon as={FaCheckCircle} /> : <Box width='1.25em' display='inline-block' />}
          Trial-level
        </MenuItem>}
      {sites.map(s =>
        <MenuItem key={s._id} value={s._id} onClick={() => toggle(s._id)}>
          {value.includes(s._id) ? <Icon as={FaCheckCircle} /> : <Box width='1.25em' display='inline-block' />}
          {s.name}
        </MenuItem>)}
    </Menu>
  )
}

// Dialog to copy one or more existing files to other site(s). Each copy becomes
// an independent draft file doc (approvals and assignees are not carried over).
function CopyToSiteDialog ({ files, isOpen, onClose, onCopied }) {
  const { user, trial, sites } = useStore()
  const [targets, setTargets] = useState([])
  const [fullHistory, setFullHistory] = useState(false)
  const [copying, setCopying] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setTargets([])
      setFullHistory(false)
      setCopying(false)
    }
  }, [isOpen])

  const writableSites = sites?.filter(s => utils.canWriteSiteFiles(user, trial, s)) || []

  function confirm () {
    if (!files || files.length === 0 || targets.length === 0) return
    setCopying(true)
    Promise.all(files.map(file => new Promise((resolve, reject) => {
      api.files.copyToSite(file._id, { sites: targets, fullHistory }, ({ files: created }) => resolve(created || []), reject)
    })))
      .then(results => {
        const created = results.flat()
        utils.toast('success', `Copied to ${targets.length} destination${targets.length > 1 ? 's' : ''}`)
        onCopied(created)
        onClose()
      })
      .catch(err => utils.toast('error', 'Unable to copy file', err.message))
      .finally(() => setCopying(false))
  }

  return (
    <SimpleModal
      isOpen={isOpen} onClose={onClose}
      header={files?.length > 1 ? `Copy ${files.length} files to site(s)` : 'Copy file to site(s)'}
      body={
        <Stack gap={5}>
          <Text>Choose the destination site(s). Each copy is created as an independent draft — approvals and assignees are not carried over.</Text>
          <FormControl label='Destinations'>
            <SiteMultiSelect
              value={targets}
              trialWritable={utils.canWriteTrialFiles(user, trial)}
              sites={writableSites}
              onChange={setTargets}
            />
          </FormControl>
          <Checkbox isChecked={fullHistory} onChange={e => setFullHistory(e.checked)}>Copy full version history (otherwise only the latest version)</Checkbox>
        </Stack>
      }
      footer={
        <Stack direction='row' justifyContent='end'>
          <Button onClick={onClose} variant='ghost'>Cancel</Button>
          <Button colorPalette='primary' isLoading={copying} disabled={targets.length === 0} onClick={confirm}>Copy</Button>
        </Stack>
      }
    />
  )
}
